// v10/src/components/PilotHistory.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ClipboardCheck, ChevronDown, ChevronUp, CheckCircle2,
  AlertTriangle, Clock, Loader, X, Calendar, Plane,
  FileDown, Search, Filter
} from 'lucide-react';
import { inspectionService } from '../services/apiService';

// ── PDF Generator ────────────────────────────────────────────────
const generatePDF = (insp, detail) => {
  const checked = detail.items?.filter(i => i.status === 'checked').length || 0;
  const skipped = detail.items?.filter(i => i.status === 'skipped').length || 0;
  const total   = detail.items?.length || 0;
  const pct     = total ? Math.round((checked / total) * 100) : 0;

  const bySection = (detail.items || []).reduce((acc, item) => {
    if (!acc[item.section_name]) acc[item.section_name] = [];
    acc[item.section_name].push(item);
    return acc;
  }, {});

  const sectionHTML = Object.entries(bySection).map(([section, items]) => `
    <div class="section">
      <div class="section-title">${section}</div>
      ${items.map(item => `
        <div class="item ${item.status}">
          <span class="item-icon">${item.status === 'checked' ? '✓' : item.status === 'skipped' ? '⚠' : '○'}</span>
          <span class="item-text ${item.status === 'skipped' ? 'skipped-text' : ''}">${item.item_text}</span>
        </div>
      `).join('')}
    </div>
  `).join('');

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Inspección ${insp.registration || ''} — ${insp.inspection_date}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, Arial, sans-serif; color: #1e293b; background: white; }
    .page { max-width: 680px; margin: 0 auto; padding: 40px 32px; }

    .header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 28px; padding-bottom: 20px; border-bottom: 2px solid #e2e8f0; }
    .logo { font-size: 24px; font-weight: 900; color: #1d4ed8; letter-spacing: -1px; }
    .logo span { color: #7c3aed; }
    .doc-title { font-size: 12px; color: #64748b; text-align: right; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; }

    .aircraft-card { background: #f8faff; border: 1px solid #dbeafe; border-radius: 12px; padding: 16px 20px; margin-bottom: 20px; }
    .aircraft-name { font-size: 20px; font-weight: 800; color: #1e40af; margin-bottom: 6px; }
    .aircraft-reg  { display: inline-block; background: #1d4ed8; color: white; font-size: 12px; font-weight: 700; padding: 2px 10px; border-radius: 20px; font-family: monospace; }
    .meta-row { display: flex; gap: 24px; margin-top: 10px; }
    .meta-item { font-size: 12px; color: #475569; }
    .meta-item strong { color: #1e293b; }

    .progress-card { background: white; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px 20px; margin-bottom: 20px; }
    .progress-title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #64748b; margin-bottom: 10px; }
    .progress-bar-bg { background: #f1f5f9; border-radius: 99px; height: 10px; overflow: hidden; }
    .progress-bar-fill { height: 100%; border-radius: 99px; background: linear-gradient(90deg, #2563eb, #22c55e); }
    .progress-stats { display: flex; gap: 20px; margin-top: 8px; }
    .stat-checked { color: #16a34a; font-size: 13px; font-weight: 600; }
    .stat-skipped { color: #d97706; font-size: 13px; font-weight: 600; }
    .stat-total   { color: #64748b; font-size: 13px; }
    .pct-label { float: right; font-size: 13px; font-weight: 800; color: #2563eb; }

    .section { margin-bottom: 18px; }
    .section-title { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.12em; color: #3b82f6; margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #dbeafe; }
    .item { display: flex; align-items: flex-start; gap: 8px; padding: 4px 0; }
    .item-icon { font-size: 12px; width: 16px; flex-shrink: 0; margin-top: 1px; }
    .item.checked .item-icon { color: #16a34a; }
    .item.skipped .item-icon { color: #d97706; }
    .item-text { font-size: 13px; line-height: 1.4; }
    .skipped-text { text-decoration: line-through; opacity: 0.55; }

    .remarks { background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px; padding: 14px 16px; margin-top: 20px; }
    .remarks-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #92400e; margin-bottom: 6px; }
    .remarks-text  { font-size: 13px; color: #78350f; line-height: 1.5; }

    .footer { margin-top: 36px; padding-top: 16px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
    .footer-left  { font-size: 11px; color: #94a3b8; }
    .footer-right { font-size: 11px; color: #94a3b8; text-align: right; }
    .signature-line { width: 180px; border-top: 1px solid #cbd5e1; margin-top: 32px; padding-top: 6px; font-size: 11px; color: #94a3b8; }

    @media print { .page { padding: 20px; } }
  </style>
</head>
<body>
<div class="page">
  <div class="header">
    <div class="logo">Condor<span>360</span></div>
    <div class="doc-title">Registro de Inspección Prevuelo<br>${new Date().toLocaleDateString('es-AR')}</div>
  </div>

  <div class="aircraft-card">
    <div class="aircraft-name">${insp.manufacturer} ${insp.model_name}</div>
    ${insp.registration ? `<span class="aircraft-reg">${insp.registration}</span>` : ''}
    <div class="meta-row">
      <div class="meta-item">📅 <strong>${insp.inspection_date}</strong> ${insp.inspection_time || ''}</div>
      <div class="meta-item">👤 <strong>${insp.inspector_name}</strong></div>
      ${insp.club_name ? `<div class="meta-item">🏢 <strong>${insp.club_name}</strong></div>` : ''}
    </div>
  </div>

  <div class="progress-card">
    <div class="progress-title">Resultado del Chequeo <span class="pct-label">${pct}% completado</span></div>
    <div class="progress-bar-bg"><div class="progress-bar-fill" style="width:${pct}%"></div></div>
    <div class="progress-stats">
      <span class="stat-checked">✓ ${checked} chequeados</span>
      ${skipped > 0 ? `<span class="stat-skipped">⚠ ${skipped} salteados</span>` : ''}
      <span class="stat-total">${total} ítems total</span>
    </div>
  </div>

  ${sectionHTML}

  ${detail.remarks ? `
  <div class="remarks">
    <div class="remarks-title">Observaciones</div>
    <div class="remarks-text">${detail.remarks}</div>
  </div>` : ''}

  <div class="footer">
    <div class="footer-left">
      Condor360 — Asistente Prevuelo<br>
      Aeroclub Puerto Madryn
    </div>
    <div class="footer-right">
      <div class="signature-line">Firma del piloto</div>
    </div>
  </div>
</div>
</body>
</html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
  win.onload = () => {
    win.focus();
    win.print();
  };
};

// ── Status Badge ─────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = {
    completed:  { label: 'Completada', cls: 'bg-green-100 text-green-700 border-green-200',  Icon: CheckCircle2 },
    incomplete: { label: 'Incompleta', cls: 'bg-yellow-100 text-yellow-700 border-yellow-200', Icon: AlertTriangle },
    pending:    { label: 'Pendiente',  cls: 'bg-gray-100 text-gray-600 border-gray-200',       Icon: Clock },
  }[status] || { label: status, cls: 'bg-gray-100 text-gray-600 border-gray-200', Icon: Clock };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.cls}`}>
      <cfg.Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
};

// ── Inspection Detail + PDF ───────────────────────────────────────
const InspectionDetail = ({ insp, darkMode }) => {
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    inspectionService.getById(insp.id).then(r => { setDetail(r.data.data); setLoading(false); });
  }, [insp.id]);

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try { generatePDF(insp, detail); }
    finally { setTimeout(() => setPdfLoading(false), 1000); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader className="w-5 h-5 animate-spin text-blue-500" /></div>;
  if (!detail) return null;

  const checked = detail.items?.filter(i => i.status === 'checked').length || 0;
  const skipped = detail.items?.filter(i => i.status === 'skipped').length || 0;
  const total   = detail.items?.length || 0;
  const pct     = total ? Math.round((checked / total) * 100) : 0;

  const bySection = (detail.items || []).reduce((acc, item) => {
    if (!acc[item.section_name]) acc[item.section_name] = [];
    acc[item.section_name].push(item);
    return acc;
  }, {});

  return (
    <div className={`mt-3 rounded-2xl border p-4 ${darkMode ? 'bg-gray-700/30 border-gray-600' : 'bg-gray-50 border-gray-100'}`}>
      {/* Progress */}
      <div className="mb-4">
        <div className="flex justify-between text-xs mb-1.5">
          <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Progreso del chequeo</span>
          <span className="font-bold text-blue-500">{pct}%</span>
        </div>
        <div className={`h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
          <motion.div className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full"
            initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.7 }} />
        </div>
        <div className="flex gap-4 mt-1.5 text-xs">
          <span className="text-green-600 font-medium">✓ {checked} chequeados</span>
          {skipped > 0 && <span className="text-yellow-600 font-medium">⚠ {skipped} salteados</span>}
          <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>{total} total</span>
        </div>
      </div>

      {/* Items by section */}
      <div className="space-y-3 mb-4">
        {Object.entries(bySection).map(([section, items]) => (
          <div key={section}>
            <p className={`text-xs font-bold uppercase tracking-wider mb-1.5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{section}</p>
            <div className="space-y-1">
              {items.map(item => (
                <div key={item.id} className={`flex items-center gap-2 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {item.status === 'checked' && <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />}
                  {item.status === 'skipped' && <AlertTriangle className="w-3 h-3 text-yellow-500 flex-shrink-0" />}
                  {item.status === 'pending' && <Clock className="w-3 h-3 text-gray-400 flex-shrink-0" />}
                  <span className={item.status === 'skipped' ? 'line-through opacity-50' : ''}>{item.item_text}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {detail.remarks && (
        <div className={`p-3 rounded-xl text-xs mb-4 ${darkMode ? 'bg-yellow-900/20 text-yellow-300 border border-yellow-800/30' : 'bg-yellow-50 text-yellow-800 border border-yellow-100'}`}>
          <span className="font-bold">Observaciones: </span>{detail.remarks}
        </div>
      )}

      {/* PDF Button */}
      <motion.button
        onClick={handleDownloadPDF}
        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
        disabled={pdfLoading}
        style={{
          width: '100%', padding: '10px', borderRadius: '12px', border: 'none', cursor: 'pointer',
          background: 'linear-gradient(135deg, #1d4ed8, #7c3aed)',
          color: 'white', fontWeight: 700, fontSize: '13px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          opacity: pdfLoading ? 0.7 : 1,
        }}
      >
        {pdfLoading
          ? <><Loader style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }} /> Generando PDF...</>
          : <><FileDown style={{ width: '14px', height: '14px' }} /> Descargar PDF</>
        }
      </motion.button>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────
const PilotHistory = ({ darkMode, onClose }) => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [expandedId, setExpandedId]   = useState(null);
  const [search, setSearch]           = useState('');
  const [page, setPage]               = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [total, setTotal]             = useState(0);

  useEffect(() => {
    setLoading(true);
    inspectionService.getAll({ page, limit: 10 })
      .then(r => {
        setInspections(r.data.data || []);
        setTotalPages(r.data.pages || 1);
        setTotal(r.data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page]);

  const filtered = inspections.filter(i => {
    if (!search) return true;
    const t = search.toLowerCase();
    return `${i.manufacturer} ${i.model_name}`.toLowerCase().includes(t) ||
           (i.registration || '').toLowerCase().includes(t);
  });

  const card = `rounded-2xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
        onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: '620px', maxHeight: '88vh', display: 'flex', flexDirection: 'column', borderRadius: '24px', overflow: 'hidden',
          background: darkMode ? '#111827' : 'white',
          boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
          border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)'
        }}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '12px', background: 'linear-gradient(135deg,#1d4ed8,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ClipboardCheck style={{ width: '18px', height: '18px', color: 'white' }} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: darkMode ? 'white' : '#0f172a' }}>Mis Inspecciones</h2>
              <p style={{ margin: 0, fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8' }}>{total} registros</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: darkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9', border: 'none', borderRadius: '10px', width: '34px', height: '34px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>
            <X style={{ width: '16px', height: '16px' }} />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '12px 24px', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid #f1f5f9', flexShrink: 0 }}>
          <div style={{ position: 'relative' }}>
            <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '14px', height: '14px', color: '#94a3b8' }} />
            <input
              type="text" placeholder="Buscar por aeronave o matrícula..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', paddingLeft: '36px', paddingRight: '12px', paddingTop: '9px', paddingBottom: '9px', borderRadius: '12px', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc', color: darkMode ? 'white' : '#1e293b', fontSize: '13px', outline: 'none' }}
            />
          </div>
        </div>

        {/* Stats y gráficos */}
        {!loading && inspections.length > 0 && (
          <PilotStats inspections={inspections} darkMode={darkMode} />
        )}

        {/* List */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '16px 24px' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
              <Loader style={{ width: '28px', height: '28px', color: '#3b82f6', animation: 'spin 1s linear infinite' }} />
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <ClipboardCheck style={{ width: '40px', height: '40px', margin: '0 auto 12px', color: '#94a3b8' }} />
              <p style={{ color: '#94a3b8', fontSize: '14px' }}>No hay inspecciones registradas</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {filtered.map((insp, i) => (
                <motion.div key={insp.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                  style={{ borderRadius: '16px', border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0', background: darkMode ? 'rgba(255,255,255,0.03)' : 'white', overflow: 'hidden' }}
                >
                  {/* Row */}
                  <div
                    onClick={() => setExpandedId(expandedId === insp.id ? null : insp.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', cursor: 'pointer' }}
                  >
                    <div style={{ width: '38px', height: '38px', borderRadius: '12px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: insp.status === 'completed' ? 'rgba(34,197,94,0.12)' : 'rgba(245,158,11,0.12)' }}>
                      {insp.status === 'completed'
                        ? <CheckCircle2 style={{ width: '18px', height: '18px', color: '#16a34a' }} />
                        : <AlertTriangle style={{ width: '18px', height: '18px', color: '#d97706' }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: '14px', color: darkMode ? 'white' : '#0f172a' }}>
                          {insp.manufacturer} {insp.model_name}
                        </span>
                        {insp.registration && (
                          <span style={{ fontSize: '11px', fontFamily: 'monospace', padding: '1px 8px', borderRadius: '99px', background: darkMode ? 'rgba(255,255,255,0.1)' : '#f1f5f9', color: darkMode ? '#94a3b8' : '#64748b' }}>
                            {insp.registration}
                          </span>
                        )}
                        <StatusBadge status={insp.status} />
                      </div>
                      <div style={{ display: 'flex', gap: '12px', marginTop: '4px', fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8' }}>
                        <span>📅 {insp.inspection_date}</span>
                        <span>✓ {insp.checked_items}/{insp.total_items}</span>
                      </div>
                    </div>
                    <div style={{ color: darkMode ? 'rgba(255,255,255,0.3)' : '#cbd5e1', flexShrink: 0 }}>
                      {expandedId === insp.id
                        ? <ChevronUp style={{ width: '16px', height: '16px' }} />
                        : <ChevronDown style={{ width: '16px', height: '16px' }} />}
                    </div>
                  </div>

                  {/* Detail */}
                  <AnimatePresence>
                    {expandedId === insp.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}
                        style={{ paddingLeft: '16px', paddingRight: '16px', paddingBottom: '14px', borderTop: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #f1f5f9' }}
                      >
                        <InspectionDetail insp={insp} darkMode={darkMode} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ padding: '12px 24px', borderTop: darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', flexShrink: 0 }}>
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
              style={{ padding: '8px 16px', borderRadius: '10px', border: 'none', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, background: darkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9', color: darkMode ? 'white' : '#374151', fontWeight: 600, fontSize: '13px' }}>
              ← Anterior
            </button>
            <span style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8' }}>
              {page} / {totalPages}
            </span>
            <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}
              style={{ padding: '8px 16px', borderRadius: '10px', border: 'none', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.4 : 1, background: darkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9', color: darkMode ? 'white' : '#374151', fontWeight: 600, fontSize: '13px' }}>
              Siguiente →
            </button>
          </div>
        )}
      </motion.div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </motion.div>
  );
};


// ── Mini BarChart SVG ─────────────────────────────────────────────
const BarChart = ({ data, darkMode }) => {
  if (!data || data.length === 0) return null;
  const max    = Math.max(...data.map(d => d.value), 1);
  const W = 280, H = 80, pad = 24;
  const barW   = (W - pad * 2) / data.length - 4;

  return (
    <svg viewBox={`0 0 ${W} ${H + 20}`} style={{ width: '100%', maxWidth: W }}>
      {data.map((d, i) => {
        const barH = Math.max(4, ((d.value / max) * H));
        const x    = pad + i * ((W - pad * 2) / data.length);
        const y    = H - barH;
        const isLast = i === data.length - 1;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={barH}
              rx="3"
              fill={isLast
                ? (darkMode ? '#60a5fa' : '#2563eb')
                : (darkMode ? 'rgba(99,179,237,0.35)' : 'rgba(37,99,235,0.25)')} />
            <text x={x + barW / 2} y={H + 14} textAnchor="middle"
              fontSize="8" fill={darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8'}>
              {d.label}
            </text>
            {d.value > 0 && (
              <text x={x + barW / 2} y={y - 3} textAnchor="middle"
                fontSize="8" fontWeight="700"
                fill={darkMode ? '#93c5fd' : '#1d4ed8'}>
                {d.value}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

// ── Stats Cards ───────────────────────────────────────────────────
const PilotStats = ({ inspections, darkMode }) => {
  if (!inspections || inspections.length === 0) return null;

  // Últimas 8 inspecciones para el gráfico
  const last8 = [...inspections].reverse().slice(0, 8);
  const chartData = last8.map(insp => ({
    label: new Date(insp.inspection_date).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' }).slice(0, 5),
    value: parseInt(insp.checked_items) || 0,
  }));

  // Avión más usado
  const aircraftCount = {};
  inspections.forEach(i => {
    const name = `${i.manufacturer || ''} ${i.model_name || ''}`.trim();
    aircraftCount[name] = (aircraftCount[name] || 0) + 1;
  });
  const topAircraft = Object.entries(aircraftCount).sort((a, b) => b[1] - a[1])[0];

  // Ítems observados total
  const totalObs = inspections.reduce((acc, i) => acc + (parseInt(i.observation_count) || 0), 0);

  // % completado promedio
  const avgPct = Math.round(
    inspections.reduce((acc, i) => {
      const total   = parseInt(i.total_items) || 1;
      const checked = parseInt(i.checked_items) || 0;
      return acc + (checked / total) * 100;
    }, 0) / inspections.length
  );

  const statStyle = (bg, border) => ({
    borderRadius: '14px',
    padding: '12px 14px',
    background: darkMode ? bg.dark : bg.light,
    border: `1px solid ${darkMode ? border.dark : border.light}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  });

  return (
    <div style={{ padding: '16px 20px 0', display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {/* Fila de stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
        <div style={statStyle(
          { dark: 'rgba(37,99,235,0.12)', light: 'rgba(219,234,254,0.6)' },
          { dark: 'rgba(37,99,235,0.25)', light: 'rgba(37,99,235,0.15)' }
        )}>
          <span style={{ fontSize: '20px', fontWeight: 900, color: darkMode ? '#93c5fd' : '#1d4ed8', lineHeight: 1 }}>
            {inspections.length}
          </span>
          <span style={{ fontSize: '10px', fontWeight: 600, color: darkMode ? 'rgba(147,197,253,0.6)' : '#3b82f6' }}>
            Chequeos
          </span>
        </div>
        <div style={statStyle(
          { dark: 'rgba(34,197,94,0.1)', light: 'rgba(220,252,231,0.6)' },
          { dark: 'rgba(34,197,94,0.2)', light: 'rgba(34,197,94,0.15)' }
        )}>
          <span style={{ fontSize: '20px', fontWeight: 900, color: darkMode ? '#4ade80' : '#16a34a', lineHeight: 1 }}>
            {avgPct}%
          </span>
          <span style={{ fontSize: '10px', fontWeight: 600, color: darkMode ? 'rgba(74,222,128,0.6)' : '#22c55e' }}>
            Completado
          </span>
        </div>
        <div style={statStyle(
          { dark: 'rgba(245,158,11,0.1)', light: 'rgba(254,243,199,0.6)' },
          { dark: 'rgba(245,158,11,0.2)', light: 'rgba(245,158,11,0.15)' }
        )}>
          <span style={{ fontSize: '20px', fontWeight: 900, color: darkMode ? '#fcd34d' : '#d97706', lineHeight: 1 }}>
            {totalObs}
          </span>
          <span style={{ fontSize: '10px', fontWeight: 600, color: darkMode ? 'rgba(252,211,77,0.6)' : '#f59e0b' }}>
            Observaciones
          </span>
        </div>
      </div>

      {/* Avión más usado */}
      {topAircraft && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '12px', background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(248,250,252,1)', border: darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid #e2e8f0' }}>
          <Plane style={{ width: '14px', height: '14px', color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8', flexShrink: 0 }} />
          <span style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>Avión más usado</span>
          <span style={{ marginLeft: 'auto', fontSize: '12px', fontWeight: 700, color: darkMode ? 'rgba(255,255,255,0.85)' : '#0f172a' }}>
            {topAircraft[0]} <span style={{ color: darkMode ? 'rgba(99,179,237,0.7)' : '#3b82f6', fontWeight: 600 }}>({topAircraft[1]}x)</span>
          </span>
        </div>
      )}

      {/* Gráfico últimas inspecciones */}
      {chartData.length > 1 && (
        <div style={{ padding: '12px 14px', borderRadius: '12px', background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(248,250,252,1)', border: darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid #e2e8f0' }}>
          <p style={{ margin: '0 0 8px', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>
            Ítems chequeados — últimas inspecciones
          </p>
          <BarChart data={chartData} darkMode={darkMode} />
        </div>
      )}

      <div style={{ height: '1px', background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9' }} />
    </div>
  );
};

export default PilotHistory;
